import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { useSetSession } from '../../../context/Session';
import { validateEmail, validatePassword } from '../../utils/inputValidation';
import { initiateLogin, mfaChallengeCompletion } from '../api';

const SESSION_DURATION_HOURS = {
  LONG: 30 * 24,
  SHORT: 1,
};

export const useLogin = (
  email: string,
  password: string,
  reCaptchaToken: string | undefined
) => {
  const [rememberMe, setRememberMe] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [inValidEmail, setinValidEmail] = useState(!validateEmail(email));
  const [inValidPassword, setInValidPassword] = useState(
    !validatePassword(password)
  );
  const [mfaCode, setMfaCode] = useState<string[]>(new Array(6).fill(''));
  const navigate = useNavigate();
  const setSession = useSetSession();
  const [{ loading, error }, loginUser] = useAsyncFn(
    async (
      email: string,
      password: string,
      reCaptchaToken: string,
      rememberMe: boolean
    ) => {
      const response = await initiateLogin(
        email,
        password,
        reCaptchaToken,
        rememberMe
      );
      if ('mfaToken' in response) {
        setMfaToken(response.mfaToken);
      } else {
        const expHours = rememberMe
          ? SESSION_DURATION_HOURS.LONG
          : SESSION_DURATION_HOURS.SHORT;
        setSession({ authenticated: true, ...response }, expHours);
        void navigate('/');
      }
      return response;
    },
    [navigate, setSession, setMfaToken]
  );

  const [{ error: mfaError, loading: mfaLoading }, verifyMfa] =
    useAsyncFn(async () => {
      if (!mfaToken) return;
      try {
        const code = mfaCode.join('');
        const mfaResult = await mfaChallengeCompletion(mfaToken, code);
        const { ...sessionData } = mfaResult;
        sessionData.email = email;
        const expHours = rememberMe
          ? SESSION_DURATION_HOURS.LONG
          : SESSION_DURATION_HOURS.SHORT;
        setSession({ authenticated: true, ...sessionData }, expHours);
        void navigate('/');
      } finally {
        setMfaCode(new Array(6).fill(''));
      }
    }, [mfaToken, mfaCode, setMfaCode]);

  useEffect(() => {
    setinValidEmail(!validateEmail(email));
    setInValidPassword(!validatePassword(password));
  }, [email, password]);

  const handleLoginUser = () => {
    if (!process.env.REACT_APP_DISABLE_RECAPTCHA && !reCaptchaToken) {
      return null;
    }
    void loginUser(email, password, reCaptchaToken ?? 'empty', rememberMe);
  };

  return {
    loading,
    error: error ?? mfaError,
    inValidEmail,
    inValidPassword,
    inValidReCaptchaToken: !reCaptchaToken,
    rememberMe,
    setRememberMe,
    loginUser: handleLoginUser,
    mfaToken,
    mfaCode,
    setMfaCode,
    verifyMfa,
    mfaLoading,
  };
};
